/**
 * JournalEditor — 日記頁嘅**自由書寫欄**（＋儲存狀態／重試）
 *
 * 血緣：`decisions/027`（產品負責人：「some space for the user to **record their feeling**」）
 *   ＋「儲存狀態要照實講（儲存緊／已儲存／儲存唔到）」
 * 由 `components/DiaryCard.tsx` 拆出（`P-90`：每個檔 ≤200 行）
 *
 * ⚠️ 手寫風格（`--font-hand`）＋紙紋橫線 —— **零下載**（唔會將私密文字送去第三方字型服務）
 * ⚠️ `HAND_STYLE` 由呢度 export：歌詞區（`DiaryCard`）重用同一套樣式（單一真相源）
 */
import { useTranslation } from 'react-i18next';
import type { JournalSaveState } from '../hooks/useJournalAutosave';

/** 手寫紙樣式（書寫欄／歌詞共用） */
export const HAND_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-hand, inherit)',
  lineHeight: 'var(--diary-line, 28px)',
  backgroundImage:
    'repeating-linear-gradient(transparent, transparent calc(var(--diary-line, 28px) - 1px), var(--color-divider) var(--diary-line, 28px))',
  backgroundAttachment: 'local',
  color: 'var(--color-text)',
};

export interface JournalEditorProps {
  songId: string;
  draft: string;
  saveState?: JournalSaveState;
  onChange: (text: string) => void;
  onRetry: () => void;
}

export default function JournalEditor({ songId, draft, saveState, onChange, onRetry }: JournalEditorProps) {
  const { t } = useTranslation();
  const mark =
    saveState === 'saving' || saveState === 'saved' || saveState === 'error'
      ? t(saveState === 'saving' ? 'diary_saving' : saveState === 'saved' ? 'diary_saved' : 'diary_save_failed')
      : '';
  return (
    <>
      <label htmlFor={`journal-${songId}`} className="footnote" style={{ display: 'block', fontWeight: 700 }}>
        {t('diary_write_label')}
      </label>
      <textarea
        id={`journal-${songId}`}
        data-testid="diary-journal"
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('diary_write_placeholder')}
        rows={4}
        style={{
          ...HAND_STYLE,
          width: '100%',
          marginTop: '4px',
          padding: '6px 10px 8px',
          borderRadius: '12px',
          border: '1.5px solid var(--color-divider)',
          resize: 'vertical',
          fontSize: 'var(--font-size-body, 18px)',
        }}
      />
      <p data-testid="diary-save-state" className="footnote" style={{ margin: '2px 0 10px', minHeight: '1.2em' }} role="status">
        {mark}
        {saveState === 'error' ? (
          <button type="button" onClick={onRetry} style={{ marginLeft: '8px', fontFamily: 'inherit', cursor: 'pointer' }}>
            {t('diary_retry')}
          </button>
        ) : null}
      </p>
    </>
  );
}
